/**
 * Wibblewabble design tokens — the single source of truth.
 *
 * Transcribed from the Claude Design handoff
 * (docs/design_handoff_wibblewabble/). Both the Backstage app-level unified
 * theme and the MUI v5 theme used by our product pages are built from these,
 * so colour/type/spacing stay consistent everywhere.
 *
 * Fonts: IBM Plex Sans for UI, IBM Plex Mono for anything auditable — money,
 * Jira Service Management refs, slugs, resource ids, timestamps. Loaded in public/index.html.
 */

export const font = {
  sans: "'IBM Plex Sans', system-ui, -apple-system, sans-serif",
  mono: "'IBM Plex Mono', ui-monospace, SFMono-Regular, monospace",
} as const;

export const color = {
  // Brand
  primary: '#4353C9',
  primaryHover: '#3A49B5',
  primaryTint: '#E9EBFA',
  onTint: '#3A45A8',
  brandInk: '#23285C', // the deep-indigo band
  focusRing: '#DFE3FB',

  // Neutrals
  ink: '#16181D',
  secondary: '#5B6270',
  muted: '#9AA0AD',
  disabled: '#B7BCC7',
  disabledFill: '#B9BFD9', // disabled button fill
  border: '#E6E8EE',
  borderSubtle: '#F0F1F5',
  bg: '#F6F6F8', // app canvas
  surface: '#FFFFFF',
  field: '#FBFBFC',

  // Status: main / text-on-tint / tint
  success: { main: '#1E9E5A', text: '#20714A', tint: '#DDF0E6' },
  warning: { main: '#C98A1B', text: '#8A5F10', tint: '#F6ECD9' },
  danger: { main: '#D64545', text: '#A83232', tint: '#F9E0E0' },
  info: { main: '#4353C9', text: '#3A45A8', tint: '#E9EBFA' },
  neutral: { main: '#9AA0AD', text: '#5B6270', tint: '#EEF0F4' },
} as const;

/** Role badge colours: text / bg. */
export const roleColor = {
  Owner: { text: '#3A45A8', bg: '#E9EBFA' },
  Admin: { text: '#7A4DB0', bg: '#EEE4F8' },
  Developer: { text: '#20714A', bg: '#DDF0E6' },
  Viewer: { text: '#5B6270', bg: '#EEF0F4' },
} as const;

export type RoleName = keyof typeof roleColor;

/** The brand band: deep-indigo with a radial glow overlay. Heads every screen. */
export const brandBand = {
  background: color.brandInk,
  glow: 'radial-gradient(1100px 340px at 85% -60%, rgba(102,116,232,.35), transparent)',
} as const;

/** 4px base scale (gutters: 4/8/12/16/20/24/32/40). */
export const spacingBase = 4;

export const radius = {
  sm: 7, // controls
  md: 9, // buttons
  lg: 12, // cards
  modal: 14,
  pill: 999, // badges
} as const;

/** Elevation ramp (index matches MUI shadows[] positions we set). */
export const elevation = {
  e1: '0 1px 2px rgba(22,24,29,.04)', // rest
  e2: '0 2px 8px rgba(22,24,29,.06)', // raised
  e3: '0 6px 18px rgba(35,40,92,.10)', // hover
  e4: '0 18px 50px rgba(16,18,40,.30)', // modal
} as const;

export const motion = {
  hover: '150ms ease',
  fadeUp: '250ms ease', // opacity 0->1, translateY 6px->0 on screen/tab entry
  gaugeFill: '700ms ease', // 600-800ms
  provisionLoop: '0.8s linear infinite',
  pendingPulse: '1.8s ease-in-out infinite', // opacity 1 -> .45
} as const;

/** Type scale (px / weight). Mapped to MUI slots in the themes. */
export const type = {
  display: { size: 28, weight: 600, tracking: '-0.015em' }, // band greeting
  heading: { size: 21, weight: 600, tracking: '-0.01em' }, // page & section
  cardTitle: { size: 17, weight: 600, tracking: '-0.01em' }, // product name
  body: { size: 13.5, weight: 400, line: 1.55 },
  caption: { size: 12, weight: 400 },
  overline: { size: 10.5, weight: 600, tracking: '0.07em' },
  monoData: { min: 11.5, max: 14 },
} as const;

/** Focus ring applied everywhere for WCAG AA. */
export const focusRing = `2px solid ${color.focusRing}`;
